import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import "../../../constants/index.js";
import useDateAvailCheck from "../../../hooks/useDateAvailCheck.js";
import { detectFbIab, detectLineIab, detectIosDevice } from "../../../utils/browser.js";
import { detectIncognito } from "../../../utils/detect-incognito.js";
import moment from "moment";
import { useState, useRef, useEffect, useCallback } from "react";
import Dialog from "../../utils/Dialog/Dialog.js";
const THREE_DAYS = 24 * 60 * 60 * 3;
const PWA_REMINDER_KEY = "pwa-reminder";
const PwaInstaller = ({
  children,
  reminderPeriod = THREE_DAYS,
  reminderKey = PWA_REMINDER_KEY,
  hideOnPWA = true,
  onInstalled,
  onDismissed,
  withInstructions = true,
  instructionOptions,
  onInstallable,
  ignoreDismissed = false,
  ignoreSelfCheck = false,
  skipPwaCheck = false,
  checkPwaInstalled = true,
  onBeforeInstallPrompt,
  externalInstallerPrompt
}) => {
  const [installable, setInstallable] = useState(false);
  const deferredPrompt = useRef();
  const [openExtBrowserInstruction, setOpenExtBrowserInstruction] = useState(false);
  const [openShareAddHomeScreenInstruction, setOpenShareAddHomeScreenInstruction] = useState(false);
  const [isOnBeforeInstallPromptFired, setIsOnBeforeInstallPromptFired] = useState(false);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);
  const { dueAt: nextDisplayAt, updateDueAt: setNextDisplayAt } = useDateAvailCheck(reminderKey);
  useEffect(() => {
    onInstallable == null ? void 0 : onInstallable(installable);
  }, [installable, onInstallable]);
  useEffect(() => {
    if (!checkPwaInstalled) return;
    const checkIfPwaInstalled = async () => {
      const isIncognito = await detectIncognito();
      if (isIncognito == null ? void 0 : isIncognito.isPrivate) {
        setIsPwaInstalled(false);
        return;
      }
      let isPwaInstalled2 = false;
      const isBrowserPwaSupported = "onbeforeinstallprompt" in window;
      if (window.matchMedia("(display-mode: standalone)").matches) {
        isPwaInstalled2 = true;
      }
      if (window.navigator.standalone === true) {
        isPwaInstalled2 = true;
      }
      if (isBrowserPwaSupported && !isOnBeforeInstallPromptFired && !externalInstallerPrompt) {
        isPwaInstalled2 = true;
      }
      setIsPwaInstalled(isPwaInstalled2);
    };
    checkIfPwaInstalled();
  }, [checkPwaInstalled, isOnBeforeInstallPromptFired, externalInstallerPrompt]);
  useEffect(() => {
    const setupInstallationLogic = async () => {
      if (ignoreSelfCheck) {
        setInstallable(true);
        return;
      }
      if (hideOnPWA) {
        const displayMode = sessionStorage.getItem("display-mode");
        if (displayMode === "standalone") {
          return;
        }
      }
      const isBrowserPwaSupported = "onbeforeinstallprompt" in window;
      const incognitoResult = await detectIncognito();
      const isIncognito = (incognitoResult == null ? void 0 : incognitoResult.isPrivate) || false;
      if (skipPwaCheck && (!isBrowserPwaSupported || isIncognito)) {
        onBeforeInstallPrompt == null ? void 0 : onBeforeInstallPrompt();
        const isDisplaying = nextDisplayAt ? moment().isAfter(nextDisplayAt) : true;
        if (!deferredPrompt.current) {
          setInstallable(isDisplaying || ignoreDismissed);
        }
      }
      const beforeInstallPromptHandler = (e) => {
        e.preventDefault();
        setIsOnBeforeInstallPromptFired(true);
        deferredPrompt.current = e;
        onBeforeInstallPrompt == null ? void 0 : onBeforeInstallPrompt(e);
        const isDisplaying = nextDisplayAt ? moment().isAfter(nextDisplayAt) : true;
        setInstallable(isDisplaying || ignoreDismissed);
      };
      window.addEventListener("beforeinstallprompt", beforeInstallPromptHandler);
      return () => {
        window.removeEventListener("beforeinstallprompt", beforeInstallPromptHandler);
      };
    };
    let cleanup;
    setupInstallationLogic().then((cleanupFn) => {
      cleanup = cleanupFn;
    });
    return () => {
      cleanup == null ? void 0 : cleanup();
    };
  }, [hideOnPWA, nextDisplayAt, onInstalled, ignoreDismissed, onBeforeInstallPrompt, ignoreSelfCheck, skipPwaCheck]);
  useEffect(() => {
    const appInstalledHandler = () => {
      onInstalled == null ? void 0 : onInstalled();
    };
    window.addEventListener("appinstalled", appInstalledHandler);
    return () => window.removeEventListener("appinstalled", appInstalledHandler);
  }, [ignoreSelfCheck, onInstalled]);
  const doInstall = useCallback(() => {
    const prompt = externalInstallerPrompt ?? deferredPrompt.current;
    if (!prompt) {
      console.warn("No install prompt available");
      setOpenExtBrowserInstruction(true);
      return;
    }
    setInstallable(false);
    try {
      prompt.prompt();
      prompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          onInstalled == null ? void 0 : onInstalled();
        } else {
          setInstallable(true);
          onDismissed == null ? void 0 : onDismissed();
        }
      }).catch((error) => {
        console.error("Error handling user choice:", error);
        setInstallable(true);
      });
    } catch (error) {
      console.error("Error showing install prompt:", error);
      setInstallable(true);
    }
  }, [onDismissed, onInstalled, externalInstallerPrompt]);
  const handleClose = useCallback(() => {
    setNextDisplayAt(moment().add(reminderPeriod, "seconds"));
    setInstallable(false);
  }, [reminderPeriod, setNextDisplayAt]);
  const handleInstall = useCallback(() => {
    if (detectFbIab() || detectLineIab()) {
      setOpenExtBrowserInstruction(true);
      return;
    }
    if (detectIosDevice()) {
      setOpenShareAddHomeScreenInstruction(true);
      return;
    }
    doInstall();
  }, [doInstall]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    installable && !isPwaInstalled && children(handleInstall, handleClose),
    /* @__PURE__ */ jsx(
      Dialog,
      {
        open: openExtBrowserInstruction,
        onClose: () => setOpenExtBrowserInstruction(false),
        title: instructionOptions == null ? void 0 : instructionOptions.openExtBrowser.title,
        classes: {
          "title&": "!mb-4",
          "backDrop&": "!z-[99]",
          "containerWrapper&": "!z-[99]"
        },
        children: /* @__PURE__ */ jsx(
          "video",
          {
            autoPlay: true,
            loop: true,
            muted: true,
            playsInline: true,
            className: "aspect-[800/540] rounded-xl",
            children: /* @__PURE__ */ jsx(
              "source",
              {
                src: instructionOptions == null ? void 0 : instructionOptions.openExtBrowser.videoSrc,
                type: "video/mp4"
              }
            )
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      Dialog,
      {
        open: openShareAddHomeScreenInstruction,
        onClose: () => setOpenShareAddHomeScreenInstruction(false),
        title: instructionOptions == null ? void 0 : instructionOptions.openShareAddHomeScreen.title,
        classes: {
          "title&": "!mb-4",
          "backDrop&": "!z-[99]",
          "containerWrapper&": "!z-[99]"
        },
        children: /* @__PURE__ */ jsx(
          "video",
          {
            autoPlay: true,
            loop: true,
            muted: true,
            playsInline: true,
            className: "aspect-[800/540] rounded-xl",
            children: /* @__PURE__ */ jsx(
              "source",
              {
                src: instructionOptions == null ? void 0 : instructionOptions.openShareAddHomeScreen.videoSrc,
                type: "video/mp4"
              }
            )
          }
        )
      }
    )
  ] });
};
const PwaInstaller$1 = PwaInstaller;
export {
  PwaInstaller$1 as default
};
